import React from "react";
import { SportIcon, HYDRATION_ICON } from "@/components/wizard/icons";
import { FuellingInfo } from "@/components/wizard/InfoTip";
import { litresLabel } from "@/lib/units";
import { fmtDuration, fmtDistanceFromMi } from "@/lib/format";
import { SPORTS } from "@/lib/planModel";

const SPORT_LABEL = Object.fromEntries(SPORTS.map((s) => [s.value, s.label]));
// Fuel phase colours pulled from CSS custom properties so light + dark
// modes each get a readable, high-contrast tone.
const PHASE = { pre: "var(--phase-pre)", during: "var(--phase-during)", post: "var(--phase-post)", fluid: "var(--phase-fluid)" };
const LIGHT = { green: { c: "var(--fuel-light)", label: "Light" }, amber: { c: "var(--fuel-moderate)", label: "Moderate" }, red: { c: "var(--fuel-high)", label: "High" } };

export default function FuellingDay({ day, hydrationMl, unitSystem = "metric" }) {
  if (!day) return null;
  if (!day.sessions.length) {
    return (
      <div className="mt-3 pt-3 border-t border-white/10" data-testid={`fuelling-day-${day.index}`}>
        <p className="text-[11px] uppercase tracking-[0.15em] flex items-center gap-2" style={{ color: PHASE.pre }}>
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: PHASE.pre }} /> Carb Fuelling — Rest
          <FuellingInfo />
        </p>
        <p className="text-[11px] text-zinc-300 mt-1">{day.restNote}</p>
        {hydrationMl != null && (
          <div className="mt-2" data-testid={`hydration-rest-${day.index}`}>
            <p className="text-[10px] uppercase tracking-wider flex items-center gap-1 mb-1" style={{ color: PHASE.fluid }}><HYDRATION_ICON className="h-3 w-3" /> Fluid — Daily Target</p>
            <div className="grid grid-cols-3 gap-1 text-center">
              <div className="bg-[#0A0A0A] border border-white/10 py-1"><p className="text-[11px] uppercase text-zinc-400">All day</p><p className="text-base font-mono font-bold tabular-nums" style={{ color: PHASE.fluid }}>{litresLabel(hydrationMl)}</p></div>
            </div>
          </div>
        )}
      </div>
    );
  }
  const L = LIGHT[day.light];
  return (
    <div className="mt-3 pt-3 border-t border-white/10" data-testid={`fuelling-day-${day.index}`}>
      <p className="text-[11px] uppercase tracking-[0.15em] mb-2 flex items-center gap-2" style={{ color: L.c }}>
        <span className="h-1.5 w-1.5 rounded-full" style={{ background: L.c }} /> Carb Fuelling — {L.label}
        <FuellingInfo />
      </p>
      <div className="space-y-3">
        {day.sessions.map((se, si) => (
          <div key={si}>
            <p className="text-[11px] text-zinc-300 flex items-center gap-1.5 mb-1">
              <SportIcon sport={se.sport} className="h-3.5 w-3.5 text-[#E2E30A]" /> {SPORT_LABEL[se.sport] || se.sport} · {fmtDuration(se.duration)}
              {se.tpDistanceMi ? <span className="text-zinc-400"> · {fmtDistanceFromMi(se.tpDistanceMi, unitSystem)}</span> : null}
            </p>
            <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-1">Carbs</p>
            <div className="grid grid-cols-3 gap-1 text-center">
              <div className="bg-[#0A0A0A] border border-white/10 py-1" data-testid="fuel-pill-pre"><p className="text-[10px] uppercase tracking-wider text-zinc-400">Pre</p><p className="text-base font-mono font-bold tabular-nums" style={{ color: PHASE.pre }}>{se.preG}g</p></div>
              <div className="bg-[#0A0A0A] border border-white/10 py-1" data-testid="fuel-pill-during"><p className="text-[10px] uppercase tracking-wider text-zinc-400">During</p><p className="text-base font-mono font-bold tabular-nums" style={{ color: PHASE.during }}>{se.duringPerHr ? `${se.duringPerHr}/h` : "—"}</p></div>
              <div className="bg-[#0A0A0A] border border-white/10 py-1" data-testid="fuel-pill-post"><p className="text-[10px] uppercase tracking-wider text-zinc-400">Post</p><p className="text-base font-mono font-bold tabular-nums" style={{ color: PHASE.post }}>{se.postG}g</p></div>
            </div>
            {se.preNote && <p className="text-[10px] mt-1 italic" style={{ color: PHASE.pre }}>{se.preNote}</p>}
            {se.preFluidMl != null && (
              <div className="mt-2" data-testid={`hydration-${se.sport}`}>
                <p className="text-[10px] uppercase tracking-[0.15em] flex items-center gap-1 mb-1" style={{ color: PHASE.fluid }}><HYDRATION_ICON className="h-3 w-3" /> Fluid</p>
                <div className="grid grid-cols-3 gap-1 text-center">
                  <div className="bg-[#0A0A0A] border border-white/10 py-1"><p className="text-[10px] uppercase tracking-wider text-zinc-400">Pre</p><p className="text-base font-mono font-bold tabular-nums" style={{ color: PHASE.fluid }}>{litresLabel(se.preFluidMl)}</p></div>
                  <div className="bg-[#0A0A0A] border border-white/10 py-1"><p className="text-[10px] uppercase tracking-wider text-zinc-400">During</p><p className="text-base font-mono font-bold tabular-nums" style={{ color: PHASE.fluid }}>{litresLabel(se.duringFluidPerHr)}/h</p></div>
                  <div className="bg-[#0A0A0A] border border-white/10 py-1"><p className="text-[10px] uppercase tracking-wider text-zinc-400">Post</p><p className="text-base font-mono font-bold tabular-nums" style={{ color: PHASE.fluid }}>{litresLabel(se.postFluidMl)}</p></div>
                </div>
                {se.sodiumPerHr > 0 && <p className="text-[10px] mt-1" style={{ color: PHASE.during }}>~{se.sodiumPerHr}mg sodium/h</p>}
              </div>
            )}
          </div>
        ))}
        {hydrationMl != null && (
          <p className="text-[10px] text-zinc-300 flex items-center gap-1 tabular-nums" data-testid={`hydration-total-${day.index}`}>
            <HYDRATION_ICON className="h-3 w-3" style={{ color: PHASE.fluid }} /> Daily fluid target: <span className="font-mono" style={{ color: PHASE.fluid }}>{litresLabel(hydrationMl)}</span>
          </p>
        )}
      </div>
    </div>
  );
}
